import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import countries from 'assets/constants/countries';
import { useNotification } from 'providers/NotificationProvider';
import { shipmentService } from 'services/shipment/shipmentService';
import { GeocodingDto } from 'services/shipment/shipmentService.dto';
import { userService } from 'services/user/userService';
import { UserLocationDto } from 'services/user/userService.dto';

const ZIP_MAX_LENGTH = 5;

export const useUserLocationChange = (refreshCurrentUser?: () => void) => {
  const [zipCode, setZipCode] = useState('');
  const [geocoding, setGeocoding] = useState<GeocodingDto>();
  const { showNotification } = useNotification();
  const { t } = useTranslation();

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = e.target.value.replace(/[^0-9]/g, '').substring(0, ZIP_MAX_LENGTH);
    setZipCode(sanitized);
  };

  const handleZipSubmit = () => {
    if (zipCode.length < ZIP_MAX_LENGTH) return;

    shipmentService
      .fetchGeocoding(zipCode)
      .then(({ data }) => {
        setGeocoding(data);
        setZipCode('');
      })
      .catch(() =>
        showNotification({
          title: t('common:zip-not-found', { zipCode }),
          type: 'error',
        })
      );
  };

  const updateUserLocation = (geocodingData: GeocodingDto) => {
    const locationPayload: UserLocationDto = {
      lat: geocodingData.coordinates.latitude,
      lon: geocodingData.coordinates.longitude,
      country: countries.find(country => country.value === geocodingData.countryCode)?.label,
      state: geocodingData.state?.code,
      city: geocodingData.city,
      zip: geocodingData.zipCode,
      source: 'ZIP_CODE',
    };

    userService
      .setUserLocation(locationPayload)
      .then(() => {
        refreshCurrentUser?.();
        showNotification({ title: t('myProfile:location.success'), type: 'success' });
      })
      .catch(() =>
        showNotification({
          title: t('myProfile:location.error'),
          type: 'error',
        })
      );
  };

  useEffect(() => {
    if (geocoding) {
      updateUserLocation(geocoding);
    }
  }, [geocoding]);

  return {
    zipCode,
    handleZipChange,
    handleZipSubmit,
    isSubmitDisabled: zipCode.length < ZIP_MAX_LENGTH,
  };
};
